import { Link } from "react-router-dom";
import { useEffect, useRef, useState } from "react";
import { ArrowRight, Plane, Smartphone, Brain, Shield, Users, Activity, ChevronRight, AlertCircle, CheckCircle, Zap, Youtube, Play } from "lucide-react";
import { MarketingNav, MarketingFooter } from "./MarketingLayout";
import { MODULES, MODULE_COLOR } from "./moduleData";
import "../../marketing.css";

const YT_CHANNEL = "https://www.youtube.com/channel/UCoaeo2zs2N6zx3UUyEzv08Q";

const TICKER_ITEMS = [
  "LIVE · DEL · FS 201 · Turnaround 94% complete · On Track",
  "ALERT · BOM · FS 334 · Fueling delayed 8 min · Risk: High",
  "OK · BLR · FS 782 · Departed on time · Score: 98",
  "LIVE · HYD · FS 105 · Catering boarding · 12 min to STD",
  "ALERT · MAA · FS 556 · Staff shortage · 2 tasks unassigned",
  "OK · CCU · FS 990 · Clean turnaround · 47 min total",
];

const PILLARS = [
  { icon: Plane,       label: "Live Turnaround Visibility",   color: "text-orange-400", desc: "Every aircraft on stand, every task tracked, every second." },
  { icon: Smartphone,  label: "Mobile Ramp Execution",        color: "text-blue-400",   desc: "Field staff execute from their phones. No radio, no clipboard." },
  { icon: Brain,       label: "Predict Delays Before They Happen", color: "text-purple-400", desc: "AI scores departure confidence 20 minutes before the gate." },
  { icon: Shield,      label: "Vendor & Staff Accountability", color: "text-amber-400",  desc: "Every minute is timestamped. Every SLA is scored. Automatically." },
  { icon: Activity,    label: "Aircraft Readiness in One View", color: "text-teal-400",  desc: "Supervisor sees all flights, all staff, all risks — one screen." },
];

const STATS = [
  { value: "2,400+", label: "Turnarounds Managed",    mono: true },
  { value: "94%",    label: "Avg On-Time Departure",  mono: true },
  { value: "8",      label: "Stations Controlled",    mono: true },
  { value: "340+",   label: "Ground Staff Tracked",   mono: true },
];

const ICON_MAP = { Plane, Smartphone, Brain, Shield, Users, Activity };

function LiveTicker() {
  return (
    <div className="border-y border-orange-500/10 bg-orange-500/3 py-3 overflow-hidden">
      <div className="ticker-track flex gap-8 w-max whitespace-nowrap">
        {[...TICKER_ITEMS, ...TICKER_ITEMS].map((item, i) => (
          <span key={i} className="text-[10px] marketing-font-mono text-slate-500 flex items-center gap-2">
            <span className={`w-1.5 h-1.5 rounded-full live-dot flex-shrink-0 ${item.startsWith("ALERT") ? "bg-red-500" : item.startsWith("OK") ? "bg-green-500" : "bg-orange-500"}`} />
            {item}
          </span>
        ))}
      </div>
    </div>
  );
}

function HeroMockup() {
  return (
    <div className="relative bg-[#0c1929]/60 backdrop-blur-xl border border-orange-500/15 rounded-xl overflow-hidden shadow-2xl">
      {/* Mockup header */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-white/5 bg-[#07111f]/80">
        <div className="flex items-center gap-2">
          <div className="w-2 h-2 rounded-full bg-orange-500 live-dot" />
          <span className="text-[10px] text-orange-400 marketing-font-mono uppercase font-bold">Live Turnaround Board · DEL</span>
        </div>
        <span className="text-[9px] text-slate-600 marketing-font-mono">4 flights · 2 alerts</span>
      </div>

      {/* Mock flights */}
      {[
        { fn: "FS 201", route: "BOM→DEL", stand: "3L", pct: 87, risk: "low",      status: "On Track",  col: "bg-green-500" },
        { fn: "FS 334", route: "DEL→HYD", stand: "5R", pct: 42, risk: "high",     status: "⚠ Delayed", col: "bg-red-500" },
        { fn: "FS 782", route: "BLR→DEL", stand: "2L", pct: 100, risk: "low",     status: "Complete",  col: "bg-green-500" },
        { fn: "FS 105", route: "DEL→BOM", stand: "4R", pct: 65, risk: "medium",   status: "In Prog.",  col: "bg-amber-500" },
      ].map((f, i) => (
        <div key={i} className={`flex items-center gap-3 px-4 py-3 border-b border-white/4 hover:bg-orange-500/3 transition-colors fade-in-up fade-in-up-${i + 1}`}>
          <div className="w-16 text-xs font-bold text-white marketing-font-mono">{f.fn}</div>
          <div className="text-[10px] text-slate-500 w-20">{f.route} · {f.stand}</div>
          <div className="flex-1">
            <div className="flex items-center gap-2">
              <div className="flex-1 bg-slate-700/40 rounded-full h-1">
                <div className={`h-full rounded-full ${f.col} transition-all`} style={{ width: `${f.pct}%` }} />
              </div>
              <span className="text-[9px] text-slate-500 marketing-font-mono w-7">{f.pct}%</span>
            </div>
          </div>
          <span className={`text-[9px] font-bold marketing-font-mono uppercase w-16 text-right ${f.risk === "high" ? "text-red-400" : f.risk === "medium" ? "text-amber-400" : "text-green-400"}`}>
            {f.status}
          </span>
        </div>
      ))}

      {/* Alert bar */}
      <div className="flex items-center gap-2 px-4 py-2.5 bg-red-500/8 border-t border-red-500/20">
        <AlertCircle size={11} className="text-red-400 flex-shrink-0" />
        <span className="text-[10px] text-red-400 marketing-font-mono">FS 334 · Fueling not started · ETA departure risk HIGH · AI confidence: 34%</span>
      </div>
    </div>
  );
}

export default function MarketingHome() {
  return (
    <div className="min-h-screen bg-[#07111f] text-white marketing-font-body">
      <MarketingNav />

      {/* ── HERO ───────────────────────────────────────────────── */}
      <section className="relative pt-24 pb-16 overflow-hidden grid-texture">
        {/* Background image */}
        <div className="absolute inset-0 z-0"
          style={{ background: "radial-gradient(ellipse 120% 60% at 70% 50%, rgba(249,115,22,0.06) 0%, transparent 70%)" }} />

        <div className="max-w-7xl mx-auto px-6 lg:px-10 relative z-10">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            {/* Left: Copy */}
            <div>
              <div className="inline-flex items-center gap-2 bg-orange-500/10 border border-orange-500/20 px-3 py-1.5 rounded mb-6">
                <span className="w-1.5 h-1.5 rounded-full bg-orange-500 live-dot" />
                <span className="text-[10px] text-orange-400 marketing-font-mono uppercase font-bold tracking-widest">GroundShield by Fatigue Shield</span>
              </div>

              <h1 className="text-4xl sm:text-5xl lg:text-[3.5rem] font-black text-white leading-[1.05] tracking-tight marketing-font-heading mb-6">
                Real-Time Ground<br />
                <span className="text-orange-400">Operations Control</span><br />
                for Airlines
              </h1>

              <p className="text-lg text-slate-400 leading-relaxed mb-8 max-w-xl marketing-font-body">
                From ramp execution to AI delay prediction — one platform that gives your ground ops team the visibility, accountability and tools to run a flawless turnaround. Every flight. Every station.
              </p>

              <div className="flex flex-wrap gap-3">
                <Link
                  to="/marketing/demo"
                  className="flex items-center gap-2 bg-orange-500 hover:bg-orange-400 text-white font-bold px-7 py-3.5 transition-all hover:-translate-y-0.5 shadow-lg shadow-orange-500/20"
                  data-testid="hero-demo-btn"
                >
                  Request a Demo <ArrowRight size={16} />
                </Link>
                <Link
                  to="/login"
                  className="flex items-center gap-2 border border-white/15 hover:border-white/30 text-slate-300 hover:text-white font-medium px-7 py-3.5 transition-all"
                >
                  Sign In <ChevronRight size={16} />
                </Link>
              </div>

              {/* Trust signals */}
              <div className="flex flex-wrap gap-5 mt-8 pt-8 border-t border-white/5">
                {["DGCA Fatigue Rules Built-In", "Mobile-First Ramp App", "AI Delay Prediction"].map(t => (
                  <div key={t} className="flex items-center gap-1.5 text-[11px] text-slate-500">
                    <CheckCircle size={11} className="text-green-400" /> {t}
                  </div>
                ))}
              </div>
            </div>

            {/* Right: Live mockup */}
            <div className="fade-in-up fade-in-up-2">
              <HeroMockup />
            </div>
          </div>
        </div>
      </section>

      {/* ── LIVE TICKER ────────────────────────────────────────── */}
      <LiveTicker />

      {/* ── STATS BAR ──────────────────────────────────────────── */}
      <section className="py-14 border-b border-white/5"
        style={{ backgroundImage: `url(https://images.unsplash.com/photo-1771917526751-5b694de1a2ff?auto=format&w=1600&q=60)`, backgroundSize: "cover", backgroundPosition: "center" }}>
        <div className="bg-[#07111f]/88 py-14">
          <div className="max-w-7xl mx-auto px-6 lg:px-10 grid grid-cols-2 md:grid-cols-4 gap-8">
            {STATS.map((s, i) => (
              <div key={i} className={`fade-in-up fade-in-up-${i + 1}`}>
                <div className="text-4xl font-black text-orange-400 marketing-font-mono mb-1">{s.value}</div>
                <div className="text-[11px] text-slate-500 uppercase tracking-widest marketing-font-mono">{s.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── YOUTUBE — WATCH IT IN ACTION ───────────────────────── */}
      <section className="py-20 border-b border-white/5 bg-[#04090f]">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            {/* Text */}
            <div>
              <div className="inline-flex items-center gap-2 bg-red-500/10 border border-red-500/20 px-3 py-1.5 rounded mb-6">
                <Youtube size={13} className="text-red-400" />
                <span className="text-[10px] text-red-400 marketing-font-mono uppercase font-bold tracking-widest">YouTube Channel</span>
              </div>
              <h2 className="text-3xl lg:text-4xl font-black marketing-font-heading mb-5">
                Watch it in action.<br />
                <span className="text-orange-400">Before you commit to anything.</span>
              </h2>
              <p className="text-slate-400 leading-relaxed mb-8 max-w-md">
                We're building Fatigue Shield and GroundShield in public. Feature walkthroughs, live ramp demos, AI prediction deep-dives — all on our YouTube channel.
              </p>
              <div className="space-y-3 mb-8">
                {[
                  "Turnaround Control live walkthrough",
                  "AI Delay Prediction — how it scores departure confidence",
                  "Ramp App — field execution demo",
                  "Ground Fatigue Shield — real shift monitoring",
                ].map((item, i) => (
                  <div key={i} className="flex items-center gap-2 text-sm text-slate-400">
                    <Play size={11} className="text-orange-400 shrink-0" fill="currentColor" />
                    {item}
                  </div>
                ))}
              </div>
              <a href={YT_CHANNEL} target="_blank" rel="noreferrer"
                className="inline-flex items-center gap-2 bg-red-600 hover:bg-red-500 text-white font-bold px-7 py-3.5 transition-all hover:-translate-y-0.5"
                data-testid="home-youtube-cta">
                <Youtube size={16} /> Watch on YouTube
              </a>
            </div>

            {/* Video card mockup */}
            <a href={YT_CHANNEL} target="_blank" rel="noreferrer"
              className="group relative block aspect-video bg-[#0c1929] border border-white/8 hover:border-orange-500/30 overflow-hidden transition-all hover:shadow-2xl hover:shadow-orange-500/10"
              data-testid="youtube-card">
              {/* Dark screen */}
              <div className="absolute inset-0 bg-gradient-to-br from-[#0c1929] via-[#07111f] to-[#04090f]" />
              {/* Grid texture */}
              <div className="absolute inset-0 grid-texture opacity-40" />
              {/* Fake dashboard preview */}
              <div className="absolute inset-4 border border-orange-500/20 bg-[#07111f]/80 p-4 rounded-sm">
                <div className="flex items-center gap-2 mb-3">
                  <span className="w-1.5 h-1.5 rounded-full bg-orange-500 live-dot" />
                  <span className="text-[9px] text-orange-400 marketing-font-mono uppercase tracking-widest font-bold">Live Turnaround Board · DEL</span>
                </div>
                {[
                  { flight: "FS 201", route: "BOM→DEL", pct: 87, status: "ON TRACK", color: "bg-green-500" },
                  { flight: "FS 334", route: "DEL→HYD", pct: 42, status: "DELAYED",  color: "bg-red-500" },
                  { flight: "FS 782", route: "BLR→DEL", pct: 100, status: "DONE",    color: "bg-green-400" },
                ].map((r, i) => (
                  <div key={i} className="flex items-center gap-3 py-1.5 border-b border-white/4 last:border-0">
                    <span className="text-[10px] font-bold text-white marketing-font-mono w-10">{r.flight}</span>
                    <span className="text-[9px] text-slate-600 marketing-font-mono w-14">{r.route}</span>
                    <div className="flex-1 h-1 bg-white/5 rounded-full overflow-hidden">
                      <div className={`h-full ${r.color} rounded-full transition-all`} style={{ width: `${r.pct}%` }} />
                    </div>
                    <span className={`text-[8px] font-bold marketing-font-mono ${r.status === "DELAYED" ? "text-red-400" : r.status === "DONE" ? "text-green-400" : "text-orange-400"}`}>{r.status}</span>
                  </div>
                ))}
              </div>
              {/* Play button overlay */}
              <div className="absolute inset-0 flex items-center justify-center">
                <div className="w-16 h-16 bg-red-600/90 group-hover:bg-red-500 rounded-full flex items-center justify-center transition-all group-hover:scale-110 shadow-xl shadow-red-600/40">
                  <Play size={24} className="text-white ml-1" fill="white" />
                </div>
              </div>
              {/* Channel label */}
              <div className="absolute bottom-4 left-4 right-4 flex items-center gap-2">
                <Youtube size={12} className="text-red-400 shrink-0" />
                <span className="text-[10px] text-slate-500 marketing-font-mono truncate">Fatigue Shield · Platform Demos</span>
              </div>
            </a>
          </div>
        </div>
      </section>

      {/* ── 5 PILLARS ──────────────────────────────────────────── */}
      <section className="py-20 border-b border-white/5">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <div className="mb-12">
            <div className="text-[10px] text-orange-400 marketing-font-mono uppercase tracking-widest font-bold mb-3">What GroundShield delivers</div>
            <h2 className="text-3xl lg:text-4xl font-black marketing-font-heading text-white">
              Five capabilities your ramp<br />cannot operate without
            </h2>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
            {PILLARS.map((p, i) => {
              const Icon = p.icon;
              return (
                <div key={i}
                  className={`bg-[#0f2038]/60 backdrop-blur-xl border border-orange-500/10 hover:border-orange-500/40 p-6 card-glow fade-in-up fade-in-up-${i + 1}`}>
                  <Icon size={22} className={`${p.color} mb-4`} />
                  <h3 className="text-base font-bold text-white marketing-font-heading mb-2">{p.label}</h3>
                  <p className="text-sm text-slate-500">{p.desc}</p>
                </div>
              );
            })}
            {/* CTA card */}
            <div className="bg-orange-500/10 border border-orange-500/30 p-6 card-glow fade-in-up fade-in-up-6 flex flex-col justify-between">
              <div>
                <div className="text-[10px] text-orange-400 marketing-font-mono uppercase tracking-widest font-bold mb-3">See all 7 modules</div>
                <h3 className="text-base font-bold text-white marketing-font-heading mb-2">Built for DGCA-regulated airlines</h3>
                <p className="text-sm text-slate-400">DGCA compliant. FRMS ready. Deployable in days, not months.</p>
              </div>
              <Link to="/marketing/platform"
                className="mt-4 flex items-center gap-2 text-orange-400 font-bold text-sm hover:text-orange-300 transition-colors">
                Explore Platform <ArrowRight size={14} />
              </Link>
              <Link to="/product-tree"
                className="mt-2 flex items-center gap-2 text-slate-500 text-xs hover:text-slate-300 transition-colors font-mono tracking-wider">
                View Product Tree &rarr;
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* ── MODULES GRID ───────────────────────────────────────── */}
      <section className="py-20 border-b border-white/5 grid-texture">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <div className="flex items-end justify-between mb-12">
            <div>
              <div className="text-[10px] text-orange-400 marketing-font-mono uppercase tracking-widest font-bold mb-3">The full platform</div>
              <h2 className="text-3xl lg:text-4xl font-black marketing-font-heading">7 modules. One platform.</h2>
            </div>
            <Link to="/marketing/platform" className="hidden md:flex items-center gap-2 text-slate-500 hover:text-white text-sm transition-colors">
              Platform overview <ArrowRight size={14} />
            </Link>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
            {MODULES.map((m, i) => {
              const c = MODULE_COLOR[m.color];
              return (
                <Link key={m.slug} to={`/marketing/module/${m.slug}`}
                  className={`group bg-[#0c1929]/70 border ${c.border} hover:border-opacity-60 p-5 card-glow ${c.glow} hover:shadow-lg transition-all fade-in-up fade-in-up-${(i % 4) + 1}`}
                  data-testid={`module-card-${m.slug}`}>
                  <div className={`text-[8px] marketing-font-mono uppercase tracking-widest font-bold ${c.text} mb-3`}>{m.badge}</div>
                  <h3 className="text-sm font-bold text-white marketing-font-heading mb-2 group-hover:text-orange-100 transition-colors">{m.name}</h3>
                  <p className="text-[11px] text-slate-600 leading-relaxed mb-4">{m.tagline}</p>
                  <div className={`flex items-center gap-1 text-[10px] ${c.text} font-bold`}>
                    Learn more <ChevronRight size={11} />
                  </div>
                </Link>
              );
            })}
          </div>
        </div>
      </section>

      {/* ── THE GAP ────────────────────────────────────────────── */}
      <section className="py-20 border-b border-white/5">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <div className="grid lg:grid-cols-2 gap-14 items-center">
            <div>
              <div className="text-[10px] text-orange-400 marketing-font-mono uppercase tracking-widest font-bold mb-4">Why GroundShield</div>
              <h2 className="text-3xl lg:text-4xl font-black marketing-font-heading mb-6">
                Legacy platforms were built<br />
                <span className="text-slate-500">for the office.</span><br />
                <span className="text-orange-400">We built this for the ramp.</span>
              </h2>
              <p className="text-slate-400 leading-relaxed mb-8">
                Existing aviation platforms are too complex, too enterprise-heavy and not built for ground execution. GroundShield combines aviation operations logic with modern SaaS UX and real-time dashboards — designed to help your ramp tomorrow morning.
              </p>
              <Link to="/marketing/demo"
                className="inline-flex items-center gap-2 bg-orange-500 hover:bg-orange-400 text-white font-bold px-7 py-3.5 transition-all hover:-translate-y-0.5">
                Talk to us <ArrowRight size={16} />
              </Link>
            </div>

            {/* Comparison */}
            <div className="space-y-3">
              {[
                { label: "Setup time",      old: "6-12 months",    neu: "Days, not months"         },
                { label: "Mobile ramp app", old: "None or PDF",    neu: "Native PWA, role-specific" },
                { label: "AI delay warning",old: "Not available",  neu: "20 min before STD"        },
                { label: "Fatigue tracking",old: "Manual sheets",  neu: "Auto-scored, DGCA rules"  },
                { label: "Vendor SLA data", old: "Spreadsheets",   neu: "Automatic timestamps"     },
              ].map((row, i) => (
                <div key={i} className="grid grid-cols-3 gap-3 text-sm">
                  <div className="text-slate-500 text-[11px] marketing-font-mono uppercase self-center">{row.label}</div>
                  <div className="bg-slate-800/30 border border-slate-700/20 px-3 py-2.5 text-slate-600 text-[12px] line-through text-center">{row.old}</div>
                  <div className="bg-green-500/8 border border-green-500/20 px-3 py-2.5 text-green-400 text-[12px] font-bold text-center">{row.neu}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ── FINAL CTA ──────────────────────────────────────────── */}
      <section className="py-24 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-orange-500/5 via-transparent to-transparent" />
        <div className="max-w-7xl mx-auto px-6 lg:px-10 text-center relative">
          <div className="text-[10px] text-orange-400 marketing-font-mono uppercase tracking-widest font-bold mb-4">Start today</div>
          <h2 className="text-4xl lg:text-5xl font-black marketing-font-heading mb-6">
            This will help your ramp<br />
            <span className="text-orange-400">tomorrow morning.</span>
          </h2>
          <p className="text-slate-400 max-w-xl mx-auto mb-10">
            Request a demo and see GroundShield running with real flight data from your stations — in under 30 minutes.
          </p>
          <div className="flex flex-wrap gap-4 justify-center">
            <Link to="/marketing/demo"
              className="flex items-center gap-2 bg-orange-500 hover:bg-orange-400 text-white font-bold px-9 py-4 text-base transition-all hover:-translate-y-0.5 shadow-xl shadow-orange-500/20">
              Request a Demo <ArrowRight size={17} />
            </Link>
            <a href={YT_CHANNEL} target="_blank" rel="noreferrer"
              className="flex items-center gap-2 bg-red-600/20 hover:bg-red-600/30 border border-red-500/30 hover:border-red-500/50 text-red-400 hover:text-red-300 font-bold px-9 py-4 text-base transition-all">
              <Youtube size={17} /> Watch on YouTube
            </a>
          </div>
        </div>
      </section>

      <MarketingFooter />
    </div>
  );
}
